import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import CustomerNavbar from '../components/CustomerNavbar';
import MobileFooter from '../components/MobileFooter';
import axios from 'axios';
import { getFileUrl, getApiUrl } from '../config/api';

interface Caregiver {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profilePhoto: string;
  servicesOffered: string[];
  yearsOfExperience: number;
  hourlyRate?: { min: number; max: number };
  rating: { average: number; count: number };
  isVerified: boolean;
  bio?: string;
  languages?: string[];
  certifications?: string[];
  acceptsDigitalPayment?: boolean;
  availability?: {
    monday?: boolean;
    tuesday?: boolean;
    wednesday?: boolean;
    thursday?: boolean;
    friday?: boolean;
    saturday?: boolean;
    sunday?: boolean;
  };
  experiences?: {
    years: number;
    role: string;
    place: string;
  }[];
}

interface Review {
  _id: string;
  customer: {
    firstName: string;
    lastName: string;
  };
  rating: number;
  reviewText?: string;
  createdAt: string;
  reviewedBy: 'customer' | 'caregiver';
  job?: {
     title: string;
     serviceType: string;
  };
}

interface UnavailablePeriod {
  startDate: string;
  endDate: string;
}

const CaregiverProfile: React.FC = () => {
  usePageTitle('Caregiver Profile');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [caregiver, setCaregiver] = useState<Caregiver | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [unavailablePeriods, setUnavailablePeriods] = useState<UnavailablePeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  
  // Booking form state
  const [bookingData, setBookingData] = useState({
    serviceType: '',
    title: '',
    description: '',
    contractType: 'monthly' as 'weekly' | 'monthly',
    startDate: '',
    endDate: '',
    startTime: '09:00',
    endTime: '17:00',
    preferredTime: '09:00-17:00',
    paymentMethod: 'cash'
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<{start: Date | null, end: Date | null}>({start: null, end: null});
  const [imageLoadError, setImageLoadError] = useState(false);
  const [customerBalance, setCustomerBalance] = useState(0);

  // Auto-calculate end date based on contract type and start date
  useEffect(() => {
     const fetchBalance = async () => {
         try {
             // Only if user is customer (not viewing as public or caregiver)
             // We can check token existence or rely on auth context in future
             const token = localStorage.getItem('token');
             if (token) {
                 const response = await axios.get(getApiUrl('/api/wallet/balance'), {
                     headers: { Authorization: `Bearer ${token}` }
                 });
                 setCustomerBalance(response.data.balance || 0);
             }
         } catch (err) {
             console.error('Failed to fetch balance', err);
         }
     };
     fetchBalance();
  }, []);

  useEffect(() => {
    if (bookingData.startDate) {
      const startDate = new Date(bookingData.startDate);
      const endDate = new Date(startDate);
      
      if (bookingData.contractType === 'weekly') {
        // Weekly = 7 days inclusive (Start + 6 days)
        endDate.setDate(endDate.getDate() + 6);
      } else {
        // Monthly = 30 days inclusive (Start + 29 days)
        // Using fixed 30 days to avoid month length variations (28/29/31)
        endDate.setDate(endDate.getDate() + 29);
      }
      
      const endDateStr = endDate.toISOString().split('T')[0];
      setBookingData(prev => ({
        ...prev,
        endDate: endDateStr
      }));

      // Check if selected period is available
      const conflict = checkDateAvailability(bookingData.startDate, endDateStr);
      if (conflict) {
        setBookingError(`This caregiver is unavailable during this period (${new Date(conflict.startDate).toLocaleDateString()} - ${new Date(conflict.endDate).toLocaleDateString()}). Please select different dates.`);
      } else {
        setBookingError('');
      }
    }
  }, [bookingData.startDate, bookingData.contractType, unavailablePeriods]);

  // Auto-calculate default end time when start time changes
  useEffect(() => {
    if (bookingData.startTime) {
      const [hours, minutes] = bookingData.startTime.split(':').map(Number);
      const endHours = (hours + 8) % 24;
      const newEndTime = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      
      setBookingData(prev => ({
        ...prev,
        endTime: newEndTime,
        preferredTime: `${bookingData.startTime}-${newEndTime}`
      }));
    }
  }, [bookingData.startTime]);

  // Update preferredTime when endTime changes
  useEffect(() => {
    if (bookingData.endTime && bookingData.startTime) {
      setBookingData(prev => ({
        ...prev,
        preferredTime: `${prev.startTime}-${prev.endTime}`
      }));
    }
  }, [bookingData.endTime]);

  useEffect(() => {
    const fetchCaregiverData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Reset image error state
        setImageLoadError(false);
        
        // Fetch caregiver details
        const caregiverResponse = await axios.get(`/api/caregivers/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const caregiverData = caregiverResponse.data.data || caregiverResponse.data;
        setCaregiver(caregiverData);

        // Fetch reviews
        try {
          const reviewsResponse = await axios.get(`/api/reviews/caregiver/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setReviews(reviewsResponse.data.reviews || []);
        } catch (err) {
        }

        // Fetch unavailable dates
        try {
          const unavailableResponse = await axios.get(getApiUrl(`/api/jobs/caregiver/${id}/unavailable-dates`));
          setUnavailablePeriods(unavailableResponse.data.data || []);
        } catch (err) {
        }
      } catch (error: any) {
        console.error('Error fetching caregiver:', error);
        setError('Failed to load caregiver profile');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCaregiverData();
    }
  }, [id]);

  const handleBooking = () => {
    if (caregiver) {
      setShowBookingForm(true);
      setBookingData({
        serviceType: '',
        title: '',
        description: '',
        contractType: 'monthly' as 'weekly' | 'monthly',
        startDate: '',
        endDate: '',
        startTime: '09:00',
        endTime: '17:00',
        preferredTime: '09:00-17:00',
        paymentMethod: 'cash'
      });
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBookingLoading(true);
    setBookingError('');

    try {
      const token = localStorage.getItem('token');
      if (!token || !caregiver) {
        setBookingError('Authentication required');
        return;
      }

      // Fetch customer profile to get service location
      let customerLocation;
      let customerProfile;
      try {
        const profileResponse = await axios.get(getApiUrl('/api/customers/profile'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        customerProfile = profileResponse.data.data;
        customerLocation = customerProfile?.serviceLocation;
        
        if (!customerLocation || !customerLocation.coordinates || !customerLocation.address) {
          setBookingError('Please update your profile with a service address before booking. Go to Profile > Service Location.');
          setBookingLoading(false);
          return;
        }
      } catch (profileError) {
        console.error('Error fetching customer profile:', profileError);
        setBookingError('Unable to fetch your profile information. Please try again.');
        setBookingLoading(false);
        return;
      }

      // Use customer location for the job
      // Note: Patient details are available in the customer profile linked to the job
      
      const rate = bookingData.contractType === 'weekly' ? 8000 : 22000;

      // Balance Check for Digital Payments
      if (bookingData.paymentMethod === 'digital') {
           const start = new Date(bookingData.startDate);
           const end = new Date(bookingData.endDate);
           const diffTime = Math.abs(end.getTime() - start.getTime());
           const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // inclusive
           
           let totalCost = 0;
           if (bookingData.contractType === 'weekly') {
               totalCost = (rate / 7) * diffDays;
           } else {
               totalCost = (rate / 30) * diffDays;
           }
           totalCost = Math.round(totalCost);
           
           if (customerBalance < totalCost) {
                setBookingError(`Insufficient wallet balance. REQUIRED: ৳${totalCost.toLocaleString()}, AVAILABLE: ৳${customerBalance.toLocaleString()}. Please top up your wallet.`);
                setBookingLoading(false);
                return;
           }
      }

      const jobData = {
        assignedCaregiver: caregiver._id,
        serviceType: bookingData.serviceType,
        title: bookingData.title,
        description: bookingData.description, // Just the description, no appended text
        location: customerLocation, // Include customer's service location
        contract: {
          type: bookingData.contractType,
          rate: rate
        },
        paymentMethod: bookingData.paymentMethod,
        schedule: {
          startDate: new Date(bookingData.startDate).toISOString(),
          endDate: new Date(bookingData.endDate).toISOString(),
          preferredTime: bookingData.preferredTime
        }
      };

      await axios.post(getApiUrl('/api/jobs'), jobData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Booking request submitted successfully!');
      setShowBookingForm(false);
      setBookingData({
        serviceType: '',
        title: '',
        description: '',
        contractType: 'monthly',
        startDate: '',
        endDate: '',
        startTime: '09:00',
        endTime: '17:00',
        preferredTime: '09:00-17:00',
        paymentMethod: 'cash'
      });
    } catch (error: any) {
      console.error('Booking error:', error);
      setBookingError(error.response?.data?.message || error.response?.data?.error || 'Failed to submit booking request');
    } finally {
      setBookingLoading(false);
    }
  };

  const serviceTypeNames: Record<string, string> = {
    'nurse-care': 'Nurse Care',
    'everyday-assistance': 'Everyday Assistance'
  };

  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Check if selected dates overlap with unavailable periods
  const checkDateAvailability = (startDate: string, endDate: string): UnavailablePeriod | null => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (const period of unavailablePeriods) {
      const periodStart = new Date(period.startDate);
      const periodEnd = new Date(period.endDate);
      
      // Check for overlap
      if (start <= periodEnd && end >= periodStart) {
        return period;
      }
    }
    return null;
  };

  // Get minimum selectable date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Generate calendar data for the next 3 months
  const generateCalendarMonths = () => {
    const months = [];
    const today = new Date();
    
    for (let i = 0; i < 3; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push(date);
    }
    
    return months;
  };

  // Check if a specific date falls within unavailable periods
  const isDateUnavailable = (date: Date): boolean => {
    for (const period of unavailablePeriods) {
      const periodStart = new Date(period.startDate);
      const periodEnd = new Date(period.endDate);
      periodStart.setHours(0, 0, 0, 0);
      periodEnd.setHours(23, 59, 59, 999);
      
      if (date >= periodStart && date <= periodEnd) {
        return true;
      }
    }
    return false;
  };

  // Get days in a month as calendar grid
  const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty slots for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Handle date click in calendar
  const handleDateClick = (date: Date) => {
    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
    const isUnavailable = isDateUnavailable(date);
    
    if (isPast || isUnavailable) return;
    
    // Calculate end date based on contract type
    const endDate = new Date(date);
    if (bookingData.contractType === 'weekly') {
      // 7 days including start date, so add 6 days
      endDate.setDate(endDate.getDate() + 6);
    } else {
      // 30 days including start date, so add 29 days
      endDate.setDate(endDate.getDate() + 29);
    }
    
    // Check if the entire range is available
    const rangeStart = new Date(date);
    const rangeEnd = new Date(endDate);
    let hasConflict = false;
    
    while (rangeStart <= rangeEnd) {
      if (isDateUnavailable(rangeStart)) {
        hasConflict = true;
        break;
      }
      rangeStart.setDate(rangeStart.getDate() + 1);
    }
    
    if (hasConflict) {
      setBookingError(`Cannot book from ${date.toLocaleDateString()} - the ${bookingData.contractType} period would overlap with an existing booking.`);
      return;
    }
    
    // Set the dates (set to noon to avoid timezone issues)
    const startDate = new Date(date);
    startDate.setHours(12, 0, 0, 0);
    const endDateWithTime = new Date(endDate);
    endDateWithTime.setHours(12, 0, 0, 0);
    
    setSelectedDates({ start: startDate, end: endDateWithTime });
    setBookingData({
      ...bookingData,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDateWithTime.toISOString().split('T')[0]
    });
    setBookingError('');
  };

  // Navigate calendar months
  const goToNextMonth = () => {
    setCurrentCalendarMonth(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      return next;
    });
  };

  const goToPreviousMonth = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    setCurrentCalendarMonth(prev => {
      const previous = new Date(prev);
      previous.setMonth(previous.getMonth() - 1);
      
      // Don't go before current month
      if (previous < today) {
        return new Date(today.getFullYear(), today.getMonth(), 1);
      }
      return previous;
    });
  };

  // Check if date is in selected range
  const isDateInSelectedRange = (date: Date): boolean => {
    if (!selectedDates.start || !selectedDates.end) return false;
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const start = new Date(selectedDates.start);
    start.setHours(0, 0, 0, 0);
    const end = new Date(selectedDates.end);
    end.setHours(0, 0, 0, 0);
    return checkDate >= start && checkDate <= end;
  };

  // Check if date is the start of selected range
  const isDateSelectedStart = (date: Date): boolean => {
    if (!selectedDates.start) return false;
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const start = new Date(selectedDates.start);
    start.setHours(0, 0, 0, 0);
    return checkDate.getTime() === start.getTime();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
        
        <div className="flex justify-center items-center flex-grow">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error || !caregiver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
        
        <div className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Caregiver not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col animate-fade-in">
      

      <div className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full pb-20 sm:pb-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
        >
          <div className="w-8 h-8 rounded-full bg-white shadow-sm group-hover:bg-white/80 flex items-center justify-center mr-3 transition-colors">
            <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </div>
          <span className="font-medium">Back</span>
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Profile Details Card (Moved first for Mobile Layout) */}
          <div className="lg:col-span-1 lg:col-start-3 lg:row-start-1">
            <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-3xl shadow-2xl overflow-hidden sticky top-8">
              {/* Beautiful Profile Header */}
              <div className="bg-white/95 backdrop-blur-sm p-6">
                <div className="flex flex-col items-center text-center">
                  {caregiver.profilePhoto && !imageLoadError ? (
                    <img
                      src={getFileUrl(caregiver.profilePhoto)}
                      alt={`${caregiver.firstName} ${caregiver.lastName}`}
                      className="w-24 h-24 rounded-full object-cover border-4 border-primary-500 shadow-lg mb-4"
                      onError={(e) => {
                        console.error('Failed to load profile photo:', caregiver.profilePhoto);
                        console.error('Attempted URL:', getFileUrl(caregiver.profilePhoto));
                        setImageLoadError(true);
                      }}
                    />
                  ) : (
                    <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center border-4 border-primary-500 shadow-lg mb-4">
                      <span className="text-3xl font-bold text-primary-600">
                        {caregiver.firstName?.charAt(0)}{caregiver.lastName?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-gray-900 tracking-wide uppercase mb-2">
                    {caregiver.firstName} {caregiver.lastName}
                  </h3>
                  {caregiver.isVerified && (
                    <span className="inline-flex items-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Verified
                    </span>
                  )}
                  
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="font-bold text-gray-900">{caregiver.rating?.average?.toFixed(1) || '0.0'}</span>
                      <span className="text-gray-600 text-sm ml-1">({caregiver.rating?.count || 0})</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 mr-1 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-semibold">{caregiver.yearsOfExperience} years</span>
                    </div>
                  </div>
                  
                  {caregiver.bio && (
                    <p className="text-gray-600 text-sm mt-4 leading-relaxed">{caregiver.bio}</p>
                  )}
                </div>
              </div>

              {/* Pricing */}
              <div className="px-6 pt-8 pb-6">
                {caregiver.hourlyRate && (
                  <div className="text-center mb-6">
                    <div className="text-white/80 text-sm font-medium mb-1">Hourly Rate</div>
                    <div className="text-4xl font-bold text-white">
                      ${caregiver.hourlyRate.min} - ${caregiver.hourlyRate.max}
                    </div>
                    <div className="text-white/70 text-sm mt-1">per hour</div>
                  </div>
                )}

                <button
                  onClick={handleBooking}
                  className="w-full bg-white text-primary-700 py-4 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all transform hover:scale-105 shadow-lg mb-4"
                >
                  Book This Caregiver
                </button>

                <div className="space-y-3 text-white">
                  <div className="flex items-center text-sm bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="break-all">{caregiver.phone}</span>
                  </div>
                  <div className="flex items-center text-sm bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="break-all">{caregiver.email}</span>
                  </div>
                </div>
              </div>

              {/* Experience & Certifications */}
              <div className="px-6 pb-8 space-y-4">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm text-center">
                    <div className="text-2xl font-bold text-white mb-1">{caregiver.yearsOfExperience}+</div>
                    <div className="text-xs text-primary-100 uppercase tracking-wide">Years Exp</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm text-center">
                    <div className="text-2xl font-bold text-white mb-1">{caregiver.rating.count}</div>
                    <div className="text-xs text-primary-100 uppercase tracking-wide">Jobs Done</div>
                  </div>
                </div>

                {caregiver.experiences && caregiver.experiences.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-white mb-3 text-lg border-b border-white/20 pb-2">Experience</h3>
                    <div className="space-y-3">
                      {caregiver.experiences.map((exp: any, i: number) => (
                        <div key={i} className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                          <h4 className="font-bold text-white text-sm">{exp.role}</h4>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-primary-100 text-xs">{exp.place || 'Private Care'}</span>
                            <span className="text-white text-xs bg-white/20 px-2 py-0.5 rounded-full">{exp.years} yrs</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Left Column - Content (Services & Reviews) */}
          <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1 space-y-6">
            {/* Services Offered */}
            <div className="bg-white rounded-2xl shadow-lg p-8 card-hover">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Services Offered</h2>
              <div className="flex flex-wrap gap-3">
                {caregiver.servicesOffered && caregiver.servicesOffered.length > 0 ? (
                  caregiver.servicesOffered.map((service) => (
                    <span
                      key={service}
                      className="bg-primary-100 text-primary-700 px-4 py-2 rounded-full font-medium"
                    >
                      {serviceTypeNames[service] || service}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500">No services listed</p>
                )}
              </div>
            </div>

            {/* Languages */}
            {caregiver.languages && caregiver.languages.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8 card-hover">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Languages</h2>
                <div className="flex flex-wrap gap-2">
                  {caregiver.languages.map((language, index) => (
                    <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      {language}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews */}
            <div className="bg-white rounded-2xl shadow-lg p-8 card-hover">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Reviews & Ratings</h2>
              {reviews.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No reviews yet</p>
              ) : (
                <>
                  {/* Rating Summary */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-8">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Overall Rating */}
                      <div className="flex flex-col items-center justify-center">
                        <div className="text-5xl font-bold text-gray-900 mb-2">
                          {caregiver?.rating.average.toFixed(1)}
                        </div>
                        <div className="flex items-center mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg
                              key={star}
                              className={`w-6 h-6 ${
                                star <= Math.round(caregiver?.rating.average || 0)
                                  ? 'text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <p className="text-gray-600 text-sm">
                          Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                        </p>
                      </div>

                      {/* Rating Breakdown */}
                      <div className="space-y-2">
                        {[5, 4, 3, 2, 1].map((starLevel) => {
                          // Only count reviews from customers for the rating distribution
                          const customerReviews = reviews.filter(r => r.reviewedBy === 'customer');
                          const count = customerReviews.filter((r) => r.rating === starLevel).length;
                          const percentage = customerReviews.length > 0 ? (count / customerReviews.length) * 100 : 0;
                          return (
                            <div key={starLevel} className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-700 w-8">
                                {starLevel}
                                <svg className="w-4 h-4 inline text-yellow-400 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              </span>
                              <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div
                                  className="bg-yellow-400 h-full transition-all duration-300"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium text-gray-600 w-8 text-right">
                                {count}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Individual Reviews */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900">Reviews History</h3>
                    {reviews.map((review) => (
                      <div 
                        key={review._id} 
                        className={`border rounded-xl p-6 hover:shadow-md transition-shadow relative ${
                          review.reviewedBy === 'caregiver' ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-200'
                        }`}
                      >
                        {review.reviewedBy === 'caregiver' && (
                          <span className="absolute top-4 right-4 text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                            Caregiver's Shift Report
                          </span>
                        )}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              review.reviewedBy === 'caregiver' ? 'bg-blue-100' : 'bg-primary-100'
                            }`}>
                              <span className={`text-sm font-bold ${
                                review.reviewedBy === 'caregiver' ? 'text-blue-600' : 'text-primary-600'
                              }`}>
                                {review.reviewedBy === 'caregiver' 
                                  ? caregiver?.firstName?.charAt(0) 
                                  : review.customer.firstName?.charAt(0)}
                                {review.reviewedBy === 'caregiver' 
                                  ? caregiver?.lastName?.charAt(0) 
                                  : review.customer.lastName?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {review.reviewedBy === 'caregiver' 
                                  ? `${caregiver?.firstName} (Caregiver)` 
                                  : `${review.customer.firstName} ${review.customer.lastName}`}
                              </p>
                              <div className="flex items-center gap-2 text-gray-500 text-sm">
                                <span>{new Date(review.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}</span>
                                {review.job?.serviceType && (
                                  <>
                                    <span>•</span>
                                    <span className="text-primary-600 font-medium">
                                      {serviceTypeNames[review.job.serviceType] || review.job.serviceType}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Only show star rating badge if it is a customer review, or label it if caregiver */}
                          <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1 rounded-full">
                            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="font-semibold text-yellow-700">
                              {review.rating}.0
                              {review.reviewedBy === 'caregiver' && <span className="text-xs ml-1 text-gray-500">(given)</span>}
                            </span>
                          </div>
                        </div>
                        {review.reviewText && (
                          <p className="text-gray-700 leading-relaxed">{review.reviewText}</p>
                        )}
                        {!review.reviewText && (
                          <p className="text-gray-400 italic">No written review provided</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Booking Form Modal */}
      {showBookingForm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 pt-24 pb-24 z-[100] backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-full overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-1">Book Caregiver</h2>
                  <p className="text-white/80 text-sm">Complete the form to send your booking request</p>
                </div>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full p-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto flex-grow">

              <form onSubmit={handleBookingSubmit} className="space-y-6">
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-5 border border-primary-200">
                  <div className="flex items-center gap-4">
                    {caregiver?.profilePhoto && !imageLoadError ? (
                      <img
                        src={getFileUrl(caregiver.profilePhoto)}
                        alt={`${caregiver.firstName} ${caregiver.lastName}`}
                        className="w-16 h-16 rounded-full object-cover border-2 border-primary-500"
                        onError={(e) => {
                          console.error('Failed to load profile photo in modal:', caregiver.profilePhoto);
                          console.error('Attempted URL:', getFileUrl(caregiver.profilePhoto));
                          setImageLoadError(true);
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-primary-200 rounded-full flex items-center justify-center border-2 border-primary-500">
                        <span className="text-xl font-bold text-primary-700">
                          {caregiver?.firstName?.charAt(0)}{caregiver?.lastName?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Booking for:</p>
                      <p className="text-xl font-bold text-gray-900">{caregiver?.firstName} {caregiver?.lastName}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(caregiver?.servicesOffered && caregiver.servicesOffered.length > 0
                      ? caregiver.servicesOffered
                      : ['nurse-care', 'everyday-assistance']
                    ).map((service) => (
                      <button
                        key={service}
                        type="button"
                        onClick={() => setBookingData({ ...bookingData, serviceType: service })}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 group ${
                          bookingData.serviceType === service
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-gray-50'
                        }`}
                      >
                         <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                            bookingData.serviceType === service ? 'bg-primary-200 text-primary-700' : 'bg-gray-100 text-gray-500 group-hover:bg-primary-100 group-hover:text-primary-600'
                         }`}>
                           {service === 'nurse-care' ? (
                             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                             </svg>
                           ) : (
                             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                             </svg>
                           )}
                         </div>
                         <div className="flex-1 min-w-0">
                            <div className={`font-bold truncate ${bookingData.serviceType === service ? 'text-primary-900' : 'text-gray-900'}`}>
                              {serviceTypeNames[service] || service}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {service === 'nurse-care' ? 'Medical assistance & health monitoring' : 'Daily living activities & support'}
                            </div>
                         </div>
                         {bookingData.serviceType === service && (
                            <div className="absolute top-2 right-2 text-primary-500">
                               <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                            </div>
                         )}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={bookingData.title}
                    onChange={(e) => setBookingData({ ...bookingData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Daily Care for Elderly Parent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={bookingData.description}
                    onChange={(e) => setBookingData({ ...bookingData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe your care requirements..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract Type *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setBookingData({ ...bookingData, contractType: 'weekly' })}
                      className={`relative p-5 border-2 rounded-xl transition-all h-full flex flex-col items-center justify-center gap-1 group overflow-hidden ${
                        bookingData.contractType === 'weekly'
                          ? 'border-primary-500 bg-primary-50 shadow-md transform scale-[1.02]'
                          : 'border-gray-200 bg-white hover:border-primary-200 hover:shadow-lg hover:-translate-y-1'
                      }`}
                    >
                      {bookingData.contractType === 'weekly' && (
                        <div className="absolute top-3 right-3 text-primary-500">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                        </div>
                      )}
                      <div className="text-lg font-bold text-gray-900">Weekly</div>
                      <div className="text-3xl font-extrabold text-primary-600 my-1">৳8,000</div>
                      <div className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">8 hours/day</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookingData({ ...bookingData, contractType: 'monthly' })}
                      className={`relative p-5 border-2 rounded-xl transition-all h-full flex flex-col items-center justify-center gap-1 group overflow-hidden ${
                        bookingData.contractType === 'monthly'
                          ? 'border-primary-500 bg-primary-50 shadow-md transform scale-[1.02]'
                          : 'border-gray-200 bg-white hover:border-primary-200 hover:shadow-lg hover:-translate-y-1'
                      }`}
                    >
                       {bookingData.contractType === 'monthly' && (
                        <div className="absolute top-3 right-3 text-primary-500">
                           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                        </div>
                      )}
                      <div className="text-lg font-bold text-gray-900">Monthly</div>
                      <div className="text-3xl font-extrabold text-primary-600 my-1">৳22,000</div>
                      <div className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">8 hours/day</div>
                    </button>
                  </div>
                </div>



                {unavailablePeriods.length >= 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start mb-3">
                      <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 text-sm mb-2">Select Your Booking Dates</h4>
                        <p className="text-xs text-blue-800 mb-3">Click on an available (green) date to book. Red dates are already booked.</p>
                        
                        {/* Interactive calendar */}
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <button
                              type="button"
                              onClick={goToPreviousMonth}
                              disabled={currentCalendarMonth.getMonth() === new Date().getMonth() && currentCalendarMonth.getFullYear() === new Date().getFullYear()}
                              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                            <h5 className="text-sm font-semibold text-gray-700">
                              {currentCalendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </h5>
                            <button
                              type="button"
                              onClick={goToNextMonth}
                              className="p-1 rounded hover:bg-gray-100"
                            >
                              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-7 gap-1">
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                              <div key={i} className="text-xs font-semibold text-gray-400 text-center py-1">
                                {day}
                              </div>
                            ))}
                            {getCalendarDays(currentCalendarMonth.getFullYear(), currentCalendarMonth.getMonth()).map((date, i) => {
                              if (!date) return <div key={i} className="aspect-square"></div>;
                              
                              const isUnavailable = isDateUnavailable(date);
                              const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                              const isInRange = isDateInSelectedRange(date);
                              const isStartDate = isDateSelectedStart(date);
                              
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => handleDateClick(date)}
                                  disabled={isPast || isUnavailable}
                                  className={`aspect-square flex items-center justify-center text-xs rounded transition-all ${
                                    isStartDate
                                      ? 'bg-primary-600 text-white font-bold ring-2 ring-primary-400 scale-110'
                                      : isInRange
                                      ? 'bg-primary-200 text-primary-900 font-semibold'
                                      : isUnavailable
                                      ? 'bg-red-500 text-white font-semibold cursor-not-allowed'
                                      : isPast
                                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                      : 'bg-green-100 text-green-800 hover:bg-green-200 hover:scale-105 cursor-pointer'
                                  }`}
                                  title={
                                    isStartDate
                                      ? 'Selected start date'
                                      : isInRange
                                      ? 'In selected range'
                                      : isUnavailable
                                      ? 'Not available'
                                      : isPast
                                      ? 'Past date'
                                      : 'Click to book from this date'
                                  }
                                >
                                  {date.getDate()}
                                </button>
                              );
                            })}
                          </div>
                          
                          <div className="flex items-center justify-center gap-3 mt-3 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-green-100 border border-green-200 rounded"></div>
                              <span className="text-gray-600">Available</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-primary-600 rounded"></div>
                              <span className="text-gray-600">Selected</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-red-500 rounded"></div>
                              <span className="text-gray-600">Not Available</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Selected dates display */}
                        {selectedDates.start && selectedDates.end && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="text-sm font-semibold text-green-900 mb-1">Selected Period:</div>
                            <div className="text-sm text-green-800">
                              {selectedDates.start.toLocaleDateString()} - {selectedDates.end.toLocaleDateString()}
                              <span className="ml-2 text-xs">({bookingData.contractType})</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Upcoming bookings list */}
                        {unavailablePeriods.length > 0 && (
                          <div className="mt-3 text-xs text-blue-800">
                            <div className="font-semibold mb-1">Caregiver's Unavailable Periods:</div>
                            {unavailablePeriods.slice(0, 3).map((period, index) => (
                              <div key={index} className="ml-2">
                                • {new Date(period.startDate).toLocaleDateString()} - {new Date(period.endDate).toLocaleDateString()}
                              </div>
                            ))}
                            {unavailablePeriods.length > 3 && (
                              <div className="ml-2 text-blue-600 italic">+ {unavailablePeriods.length - 3} more...</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shift Timing
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group">
                      <label className="block text-xs font-semibold text-gray-500 mb-1 group-hover:text-primary-600 transition-colors">Start Time</label>
                      <input
                        type="time"
                        value={bookingData.startTime}
                        onChange={(e) => setBookingData({ ...bookingData, startTime: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-all font-medium text-gray-700 hover:border-gray-300"
                      />
                    </div>
                    <div className="relative group">
                      <label className="block text-xs font-semibold text-gray-500 mb-1 group-hover:text-blue-600 transition-colors">End Time (Max 8h)</label>
                      <input
                        type="time"
                        value={bookingData.endTime || '17:00'}
                        onChange={(e) => setBookingData({ ...bookingData, endTime: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all font-medium text-gray-700 hover:border-gray-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                {caregiver?.acceptsDigitalPayment ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Method
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Cash Payment Option */}
                        <button
                          type="button"
                          onClick={() => setBookingData(prev => ({ ...prev, paymentMethod: 'cash' }))}
                          className={`relative p-5 border-2 rounded-xl transition-all h-full flex flex-col items-center justify-center gap-2 group overflow-hidden ${
                            bookingData.paymentMethod === 'cash'
                              ? 'border-green-500 bg-green-50 shadow-md transform scale-[1.02]'
                              : 'border-gray-200 bg-white hover:border-green-200 hover:shadow-lg hover:-translate-y-1'
                          }`}
                        >
                          {bookingData.paymentMethod === 'cash' && (
                            <div className="absolute top-3 right-3 text-green-500">
                               <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                            </div>
                          )}
                          <div className={`p-3 rounded-full ${bookingData.paymentMethod === 'cash' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500 group-hover:bg-green-50 group-hover:text-green-500'} transition-colors`}>
                             <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                          </div>
                          <div className="text-center">
                              <div className="text-lg font-bold text-gray-900">Cash Payment</div>
                              <div className="text-xs text-gray-500 text-center mt-1">Pay directly to caregiver</div>
                          </div>
                        </button>

                        {/* Digital Wallet Option */}
                        <button
                          type="button"
                          onClick={() => setBookingData(prev => ({ ...prev, paymentMethod: 'digital' }))}
                          className={`relative p-5 border-2 rounded-xl transition-all h-full flex flex-col items-center justify-center gap-2 group overflow-hidden ${
                            bookingData.paymentMethod === 'digital'
                              ? 'border-primary-500 bg-primary-50 shadow-md transform scale-[1.02]'
                              : 'border-gray-200 bg-white hover:border-primary-200 hover:shadow-lg hover:-translate-y-1'
                          }`}
                        >
                          {bookingData.paymentMethod === 'digital' && (
                            <div className="absolute top-3 right-3 text-primary-500">
                               <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                            </div>
                          )}
                           <div className={`p-3 rounded-full ${bookingData.paymentMethod === 'digital' ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-600'} transition-colors`}>
                             <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                          </div>
                          <div className="text-center">
                              <div className="text-lg font-bold text-gray-900">Digital Wallet</div>
                              <div className="text-xs text-gray-500 text-center mt-1">Secure online payment</div>
                          </div>
                        </button>
                    </div>
                    
                    {bookingData.paymentMethod === 'digital' && (
                         <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between">
                            <span className="text-sm font-semibold text-blue-900">Available Wallet Balance</span>
                             <span className="text-lg font-bold text-blue-700">৳{customerBalance.toLocaleString()}</span>
                         </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 p-4 border rounded-xl bg-yellow-50 border-yellow-200">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-semibold text-yellow-800">Cash Payment Only</h4>
                        <p className="text-xs text-yellow-700 mt-1">
                          This caregiver has not enabled digital payments. Please arrange to pay in cash.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {bookingError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {bookingError}
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowBookingForm(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={bookingLoading}
                    className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {bookingLoading ? 'Submitting...' : 'Submit Booking'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Footer */}
      <footer className="hidden sm:block bg-gray-900 text-white py-8 px-4 mt-auto">
        <div className="max-w-7xl mx-auto text-center">
          <h4 className="text-2xl font-bold font-proyas tracking-wider mb-2">PROYAS</h4>
          <p className="text-gray-400 text-sm">Quality care, delivered with compassion</p>
          
          {/* Contact Info */}
          <div className="flex justify-center items-center gap-6 mt-4">
            <a href="mailto:contact@proyas.net" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
              <span className="text-sm">contact@proyas.net</span>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61586633764841" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-sm">Facebook</span>
            </a>
          </div>
          
          <p className="text-gray-500 text-xs mt-4">© 2026 <span className="font-proyas tracking-wider">PROYAS</span>. All rights reserved.</p>
        </div>
      </footer>
      
    </div>
  );
};

export default CaregiverProfile;
